<template>
  <q-table
    dense
    :class="$style.stickyHeader"
    :loading="loading"
    :filter="filterText"
    :rows-per-page-options="[0]"
    :rows="deviatingVehicleWithCanceled"
    :columns="columns"
    row-key="uuid"
    style="height: calc(100vh - 50px - 68px)"
    flat
  >
    <template v-slot:top>
      <h6 class="q-ma-none">Fordonsavvikelser med inställda tåg</h6>

      <q-space />

      <q-input
        style="width: 100%; max-width: 300px"
        debounce="300"
        v-model="filterText"
        placeholder="Sök..."
        dense
        outlined
      >
        <template v-slot:append>
          <q-icon name="mdi-magnify" />
        </template>
      </q-input>

      <q-btn
        outline
        class="q-ml-md"
        color="grey-7"
        label="Filtrera"
        icon-right="mdi-filter-menu-outline"
      >
        <DeviationVehicleWithCanceledFilterModal />
      </q-btn>

      <q-btn
        :loading="exporting"
        @click="onExport"
        class="q-ml-md"
        icon-right="mdi-file-export-outline"
        label="Export"
        color="grey-7"
        outline
      />
    </template>

    <template v-slot:header-cell-_from="props">
      <q-th :props="props" style="border-left: 1px solid rgba(0, 0, 0, 0.16)">
        {{ props.col.label }}
      </q-th>
    </template>

    <template v-slot:body-cell-_from="props">
      <q-td
        :props="props"
        class="bg-grey-3"
        style="border-left: 1px solid rgba(0, 0, 0, 0.16)"
      >
        {{ props.value }}
      </q-td>
    </template>

    <template v-slot:body-cell-_to="props">
      <q-td :props="props" class="bg-grey-3">
        {{ props.value }}
      </q-td>
    </template>

    <template v-slot:body-cell-_eventId="props">
      <q-td :props="props" class="bg-grey-3">
        <q-btn
          size="small"
          outline
          dense
          @click="
            openSearchDeviationEventNewTab({ eventUuid: props.row._eventUuid })
          "
        >
          <q-icon size="small" name="mdi-share" />
          <span>{{ props.value }}</span>
        </q-btn>
      </q-td>
    </template>
  </q-table>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { QTable } from 'quasar'
import { format } from 'date-fns'
import { exportExcel } from '@/services/export-excel'
import { useDeviationVehicle } from '@/composable/useDeviationVehicle'
import { DeviationVehicle } from '@/types/deviation-vehicle'
import { useProfile } from '@/composable/useProfile'
import { useVehicleType } from '@/composable/useVehicleType'
import { useDeviationCancel } from '@/composable/useDeviationCancel'
import { DeviationCancel } from '@/types/deviation-cancel'
import { RouteParamsRaw, useRouter } from 'vue-router'
import DeviationVehicleWithCanceledFilterModal from './DeviationVehicleWithCanceledFilterModal.vue'

export default defineComponent({
  name: 'DeviatingVehicleWithCanceledTable',

  components: {
    DeviationVehicleWithCanceledFilterModal,
  },

  setup() {
    const router = useRouter()
    const {
      data: vehicleDeviations,
      loading: loadingStateDeviationVehicle,
      // fetchAll: fetchAllDeviationVehicle,
      setDefaultPeriod: setDefaultPeriodDeviationVehicle,
      // period,
    } = useDeviationVehicle()
    const {
      fetchAll: fetchAllVehicleTypes,
      loading: loadingVehiceType,
      data: vehicleTypes,
    } = useVehicleType()
    const {
      data: deviationCancels,
      loading: loadingStateDeviationCancel,
      // fetchAll: fetchAllDeviationCancel,
      setDefaultPeriod: setDefaultPeriodDeviationCancel,
    } = useDeviationCancel()
    const { projectDependentInputFields } = useProfile()

    setDefaultPeriodDeviationVehicle()
    setDefaultPeriodDeviationCancel()
    const exporting = ref(false)

    fetchAllVehicleTypes()
    // fetchAllDeviationVehicle(period.value)
    // fetchAllDeviationCancel(period.value)

    function getVehicleTypeName(vehicleTypeUuid: string) {
      return (
        vehicleTypes.value.find(
          (vehicleType) => vehicleType.uuid === vehicleTypeUuid
        )?.name || '-'
      )
    }

    const columns = computed<QTable['columns']>(() => {
      let defaultColumns: QTable['columns'] = [
        {
          align: 'left',
          name: 'advertised',
          required: true,
          label: 'Tåguppdrag',
          field: 'advertised',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: 'departureDate',
          required: true,
          label: 'Avgångsdatum',
          field: 'departureDate',
          format: (v: string) => v && format(new Date(v), 'yyyy-MM-dd'),
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: 'from',
          required: true,
          label: 'Från plats',
          field: 'from',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: 'to',
          required: true,
          label: 'Till plats',
          field: 'to',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: 'departure',
          format: (v: string) => v && format(new Date(v), 'HH:mm'),
          required: true,
          label: 'Avg.',
          field: 'departure',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: 'arrival',
          format: (v: string) => v && format(new Date(v), 'HH:mm'),
          required: true,
          label: 'Ank.',
          field: 'arrival',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
      ]
      if (projectDependentInputFields.value.vehicleChange) {
        defaultColumns.push({
          align: 'left',
          name: 'vehicleChange',
          required: true,
          label: 'Förändring antal fordon',
          field: 'vehicleChange',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        })
      }

      if (projectDependentInputFields.value.vehicleType) {
        defaultColumns = [
          ...defaultColumns,
          {
            align: 'left',
            name: 'plannedVehicleType1',
            required: true,
            label: 'Ord. M1',
            field: 'plannedVehicleType1',
            format: getVehicleTypeName,
            sortable: true,
            style: 'width: 0px; min-width: 0px; max-width: 0px',
          },
          {
            align: 'left',
            name: 'plannedVehicleType2',
            required: true,
            label: 'Ord. M2',
            format: getVehicleTypeName,
            field: 'plannedVehicleType2',
            sortable: true,
            style: 'width: 0px; min-width: 0px; max-width: 0px',
          },
          {
            align: 'left',
            name: 'plannedVehicleType3',
            required: true,
            label: 'Ord. M3',
            format: getVehicleTypeName,
            field: 'plannedVehicleType3',
            sortable: true,
            style: 'width: 0px; min-width: 0px; max-width: 0px',
          },
        ]
      }

      const columns: QTable['columns'] = [
        ...defaultColumns,
        {
          align: 'left',
          name: 'description',
          required: true,
          label: 'Kommentar',
          field: 'description',
          sortable: true,
        },

        {
          align: 'left',
          name: '_from',
          required: true,
          label: 'Inst. från plats',
          field: '_from',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
        {
          align: 'left',
          name: '_to',
          required: true,
          label: 'Inst. till plats',
          field: '_to',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },

        {
          align: 'left',
          name: '_eventId',
          required: true,
          label: 'Inst. tåg händelse',
          field: '_eventId',
          sortable: true,
          style: 'width: 0px; min-width: 0px; max-width: 0px',
        },
      ]

      return columns
    })

    const loading = computed(() => {
      return (
        loadingStateDeviationVehicle.value.getAll ||
        loadingVehiceType.value.getAll ||
        loadingStateDeviationCancel.value.getAll
      )
    })

    function hasDeviationCancel(
      deviationVehicle: DeviationVehicle,
      deviationCancel: DeviationCancel
    ) {
      return (
        deviationVehicle.advertised === deviationCancel.advertised &&
        deviationVehicle.departureDate === deviationCancel.departureDate
      )
    }

    const deviatingVehicleWithCanceled = computed(() => {
      return vehicleDeviations.value
        .filter((deviationVehicle) => {
          return deviationCancels.value.some((deviationCancel) =>
            hasDeviationCancel(deviationVehicle, deviationCancel)
          )
        })
        .map((deviationVehicle) => {
          const foundDeviationCancel = deviationCancels.value.find(
            (deviationCancel) =>
              hasDeviationCancel(deviationVehicle, deviationCancel)
          )
          return {
            ...deviationVehicle,
            _eventId: foundDeviationCancel?.eventId,
            _eventUuid: foundDeviationCancel?.eventUuid,
            _from: foundDeviationCancel?.from,
            _to: foundDeviationCancel?.to,
          }
        })
    })

    async function onExport() {
      exporting.value = true

      const exportData = {
        name: 'Fordonsavvikelser med inställda tåg',
        sheets: [
          {
            name: 'Fordonsavvikelser med inställda tåg',
            data: deviatingVehicleWithCanceled.value.map((item) => {
              return columns.value?.reduce<{ [name: string]: unknown }>(
                (acc, col) => {
                  const value =
                    typeof col.field === 'function'
                      ? col.field(item)
                      : item[col.name as keyof DeviationVehicle]

                  if (col.format) {
                    acc[col.label] = col.format(value, null)
                  } else {
                    acc[col.label] = value
                  }

                  return acc
                },
                {}
              )
            }),
          },
        ],
      }

      exportExcel(exportData)
      exporting.value = false
    }

    function openSearchDeviationEventNewTab(params: RouteParamsRaw) {
      const route = router.resolve({
        name: 'event',
        params,
      })

      window.open(route.href, '_blank')
    }

    return {
      columns,
      loading,
      deviatingVehicleWithCanceled,
      exporting,
      onExport,
      filterText: ref(''),
      openSearchDeviationEventNewTab,
    }
  },
})
</script>

<style lang="scss" module>
.stickyHeader {
  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th {
    background-color: white;
  }

  thead tr th {
    position: sticky;
    z-index: 1;
  }

  thead tr:first-child th {
    top: 0;
  }
}
</style>
